import { ComponentProps, FC } from 'react'
import { Select as AntdSelect, Empty } from 'antd'
import cn from 'classnames'
import type { BaseOptionType, DefaultOptionType } from 'antd/es/select'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { clearOptionsFn } from './utils/options'
import {
  enhanceSelectOptionsWithColors,
  getSelectOptionSearchText,
} from './utils/selectOptionColor'
import fitContentClasses from './selectDropdownFitContent.module.css'
import optionColorClasses from './selectOptionColorLabel.module.css'

interface SelectProps<
  TValue = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> extends Omit<ComponentProps<typeof AntdSelect<TValue, OptionType>>, 'size'> {
  size?: 'middle' | 'small'
  notFoundText?: string
  enableSelectAll?: boolean
  selectAllLabel?: (type: 'select' | 'deselect') => string | React.ReactNode
  dropdownFitContentWidth?: boolean
  showOptionColors?: boolean
}

export const COMMANDS = {
  SELECT_ALL_VALUE: 'ACTION-ALL',
}

const Select: FC<SelectProps> = ({
  size,
  className,
  popupClassName,
  notFoundText,
  enableSelectAll = false,
  selectAllLabel = (type) => (
    <span style={{ fontWeight: 600 }}>
      {type === 'select' ? 'Выбрать все' : 'Отменить выбор всех'}
    </span>
  ),
  options = [],
  value,
  onChange,
  mode,
  dropdownFitContentWidth = false,
  showOptionColors = false,
  ...rest
}) => {
  const {
    dropdownMatchSelectWidth,
    optionLabelProp,
    loading,
    disabled,
    ...antdProps
  } = rest
  // options - без COMMANDS
  const clearOptions = clearOptionsFn(options)

  const handleChange = (value: any, option: any) => {
    if (
      enableSelectAll &&
      (mode === 'multiple' || mode === 'tags') &&
      onChange
    ) {
      if (Array.isArray(value) && value.includes(COMMANDS.SELECT_ALL_VALUE)) {
        if (clearOptions.length === clearOptionsFn(value)?.length) {
          onChange([], option)
          return
        }

        const allValues = options
          .filter((o: any) => o.value !== COMMANDS.SELECT_ALL_VALUE)
          .map((o: any) => o.value)
        onChange(allValues, option)
        return
      }

      onChange(value, option)
    } else {
      onChange?.(value, option)
    }
  }

  const coloredOptions = enhanceSelectOptionsWithColors(options, showOptionColors)

  const enhancedOptions =
    enableSelectAll &&
    (mode === 'multiple' || mode === 'tags') &&
    Array.isArray(coloredOptions) &&
    clearOptions.length > 0
      ? [
          {
            label: selectAllLabel(
              clearOptions.length === clearOptionsFn(value)?.length ? 'deselect' : 'select',
            ),
            value: COMMANDS.SELECT_ALL_VALUE,
          },
          ...coloredOptions,
        ]
      : coloredOptions

  const effectiveDropdownMatchSelectWidth = dropdownFitContentWidth
    ? false
    : dropdownMatchSelectWidth

  return (
    <AntdSelect
      {...antdProps}
      className={cn(
        {
          [iclasses.newSelect]: mode === undefined,
          [iclasses.newSelectMultiple]: mode === 'tags' || mode === 'multiple',

          [iclasses.newSelectSmall]: size === 'small',
          [optionColorClasses.selectionHost]: showOptionColors,
          [iclasses.newSelectLoadingLocked]: Boolean(loading) && !disabled,
        },
        className,
      )}
      loading={loading}
      disabled={disabled}
      popupClassName={cn(
        iclasses.newSelectPopup,
        popupClassName,
        dropdownFitContentWidth && fitContentClasses.dropdownFitContent,
      )}
      dropdownMatchSelectWidth={effectiveDropdownMatchSelectWidth}
      filterOption={(input, option) =>
        getSelectOptionSearchText(option)
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      notFoundContent={
        notFoundText ? (
          <Empty
            style={{ marginBlock: '8px' }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={notFoundText}
          />
        ) : undefined
      }
      mode={mode}
      value={value}
      onChange={handleChange}
      options={enhancedOptions}
      optionLabelProp={showOptionColors ? undefined : optionLabelProp}
    />
  )
}

export default Select
